import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useModal } from '@ebay/nice-modal-react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { isNilOrEmptyString, isObject } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import { setValue } from '@gef-ui/form-core/stream/transfer/control';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import SectionHeader from 'components/atoms/GefMui/SectionHeader/SectionHeader';
import { MaxLengths } from 'constants/forms';
import MODALS from 'constants/modals';
import { urls } from 'features/common/fetch/constants';
import { revertChanges, submitChanges } from 'features/views/semaphore/semaphoreFeedbackDetailModal/actions';
import {
	FIELDS,
	SEMAPHORE_FEEDBACK_DETAIL_FORM_NAME,
} from 'features/views/semaphore/semaphoreFeedbackDetailModal/form';
import { getContextUser } from 'features/views/userContext/selectors';

import LastModified from './LastModified';

export const NoteForm = ({ record }) => {
	const { note } = record;
	const dispatch = useDispatch();
	const next = useControlNext(SEMAPHORE_FEEDBACK_DETAIL_FORM_NAME);
	const semaphoreFeedbackDetailModal = useModal(MODALS.semaphore_feedback_detail);

	const contextUser = useSelector(getContextUser);
	const viewedAsSubordinateUser = !isNilOrEmptyString(contextUser);

	const formData = useFormData();
	const formIsInitiated = isObject(formData);

	useEffect(() => {
		if (formIsInitiated) {
			next(setValue({ fieldName: FIELDS.feedbackNote, value: note.text }));
		}
	}, [formIsInitiated, next, note.text]);

	const handleRevertChanges = useCallback(() => {
		dispatch(revertChanges(record));
	}, [dispatch, record]);

	const handleSave = useCallback(async () => {
		dispatch(submitChanges(record));
		semaphoreFeedbackDetailModal.hide();
	}, [dispatch, record, semaphoreFeedbackDetailModal]);

	return (
		<Stack spacing={2}>
			<SectionHeader text={getLabelMessage('semaphore:feedbackDetailNoteTitle')} />
			<LastModified date={note?.date} />
			<Grid container>
				<Grid size={12}>
					<Grid container>
						<Grid size={12}>
							<TextField
								size="small"
								fieldName={FIELDS.feedbackNote}
								label={getLabelMessage('semaphore:feedbackDetailNote')}
								fullWidth
								multiline
								rows={8}
								disabled={viewedAsSubordinateUser}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.NOTE,
									},
								}}
							/>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			<Grid container>
				<Grid sx={{ mt: 1 }} size={12}>
					{!viewedAsSubordinateUser && (
						<ComponentLoader fetchStorePath={urls.semaphoreSaveFeedbackNote} onReload={handleSave}>
							<ComponentLoader fetchStorePath={urls.semaphoreSaveFeedbackValue} onReload={handleSave}>
								<Grid
									container
									spacing={2}
									sx={{
										justifyContent: 'flex-end',
									}}
								>
									<Grid size={3}>
										<Button variant="outlined" onClick={handleRevertChanges} fullWidth>
											{getLabelMessage('semaphore:feedbackDetailRevertChanges')}
										</Button>
									</Grid>
									<Grid size={3}>
										<SubmitButton
											variant="contained"
											onClick={handleSave}
											fullWidth
											text={getLabelMessage('common:save')}
										/>
									</Grid>
								</Grid>
							</ComponentLoader>
						</ComponentLoader>
					)}
				</Grid>
			</Grid>
		</Stack>
	);
};

NoteForm.propTypes = {
	record: PropTypes.object,
};

export default NoteForm;
